import type { InputHTMLAttributes, ReactNode } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  icon?: string;
  hint?: string;
  error?: string;
  trailing?: ReactNode;
  locked?: boolean;
}

export default function Input({
  label,
  icon,
  hint,
  error,
  trailing,
  locked = false,
  className = '',
  id,
  ...rest
}: InputProps) {
  const inputId = id ?? rest.name ?? label?.replace(/\s+/g, '-').toLowerCase();
  return (
    <div className="w-full min-w-0">
      {label ? (
        <label
          htmlFor={inputId}
          className="flex items-center justify-between mb-2 text-[13px] font-semibold text-foreground-700 font-label"
        >
          <span>{label}</span>
          {locked ? (
            <span className="flex items-center gap-1 text-[11px] font-medium text-primary-700">
              <span className="flex items-center justify-center w-3.5 h-3.5">
                <i className="ri-lock-2-line text-[11px]" aria-hidden="true" />
              </span>
              {hint}
            </span>
          ) : null}
        </label>
      ) : null}
      <div className="relative">
        {icon ? (
          <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center justify-center w-4 h-4 text-foreground-400">
            <i className={`${icon} text-base`} aria-hidden="true" />
          </span>
        ) : null}
        <input
          id={inputId}
          className={`w-full box-border h-12 rounded-xl border bg-background-50 text-[15px] text-foreground-900 placeholder:text-foreground-400 transition-colors outline-none ${
            icon ? 'pl-10' : 'pl-4'
          } ${trailing ? 'pr-24' : 'pr-4'} ${
            error
              ? 'border-accent-400 focus:border-accent-500'
              : 'border-background-300 hover:border-background-400 focus:border-primary-500'
          } ${locked ? 'bg-background-200 text-foreground-600 cursor-not-allowed select-none' : ''} ${className}`}
          readOnly={locked}
          aria-readonly={locked || undefined}
          {...rest}
        />
        {trailing ? (
          <span className="absolute inset-y-0 right-3 flex items-center justify-center">{trailing}</span>
        ) : null}
      </div>
      {error ? (
        <p className="mt-1.5 flex items-start gap-1.5 text-[12.5px] text-accent-700">
          <span className="flex items-center justify-center w-3.5 h-3.5 mt-0.5 shrink-0">
            <i className="ri-error-warning-line text-[13px]" aria-hidden="true" />
          </span>
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}